/**
 * ICE server configuration for the peer connection.
 *
 * Composes: multi-STUN (always) + optional static TURN (VITE_TURN_* build-time
 * env) + short-lived TURN credentials minted by the Netlify function
 * (/.netlify/functions/turn, backed by a Cloudflare TURN key). Cached per page
 * load. Falls back cleanly to STUN-only if the endpoint is absent/unconfigured.
 */
export interface IceConfig {
  iceServers: RTCIceServer[];
  hasTurn: boolean;
}

const env = import.meta.env as Record<string, string | undefined>;
const TURN_ENDPOINT = env.VITE_TURN_ENDPOINT ?? '/.netlify/functions/turn';

let cached: IceConfig | null = null;

function staticIceServers(): RTCIceServer[] {
  const servers: RTCIceServer[] = [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun.cloudflare.com:3478'] },
  ];
  const turnUrls = (env.VITE_TURN_URLS ?? '')
    .split(',')
    .map((u) => u.trim())
    .filter(Boolean);
  if (turnUrls.length > 0 && env.VITE_TURN_USERNAME && env.VITE_TURN_CREDENTIAL) {
    servers.push({ urls: turnUrls, username: env.VITE_TURN_USERNAME, credential: env.VITE_TURN_CREDENTIAL });
  }
  return servers;
}

function computeHasTurn(servers: RTCIceServer[]): boolean {
  return servers.some((s) => {
    const urls = Array.isArray(s.urls) ? s.urls : [s.urls];
    return urls.some((u) => u.startsWith('turn:') || u.startsWith('turns:'));
  });
}

export async function getIceConfig(): Promise<IceConfig> {
  if (cached) return cached;
  const servers = staticIceServers();
  try {
    const res = await fetch(TURN_ENDPOINT, { method: 'GET' });
    if (res.ok) {
      const data = (await res.json()) as { iceServers?: RTCIceServer[] };
      if (Array.isArray(data.iceServers)) servers.push(...data.iceServers);
    }
  } catch {
    /* endpoint absent/offline → STUN + static creds only */
  }
  cached = { iceServers: servers, hasTurn: computeHasTurn(servers) };
  return cached;
}
